import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { Chip, ChipCollection } from 'seagull';
import { ClientHubIcon, DoubleIcon, FinancialCentsIcon, GmailIcon, HubSpotIcon, KarbonIcon, MakeIcon, MondayIcon, OutlookIcon, QBOIcon, SlackIcon, XeroIcon, ZapierIcon } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { useStoreActions } from '~/store/store-context';
import styles from './IntegrationsStep.module.scss';
const TOOL_KEYS = [
    'toolQuickbooksOnline',
    'toolXero',
    'toolGmail',
    'toolOutlook',
    'toolSlack',
    'toolMake',
    'toolZapier',
    'toolDouble',
    'toolClientHub',
    'toolFinancialCents',
    'toolKarbon',
    'toolMonday',
    'toolHubspot'
];
const TOOL_ICONS = {
    toolQuickbooksOnline: QBOIcon,
    toolXero: XeroIcon,
    toolGmail: GmailIcon,
    toolOutlook: OutlookIcon,
    toolSlack: SlackIcon,
    toolMake: MakeIcon,
    toolZapier: ZapierIcon,
    toolDouble: DoubleIcon,
    toolClientHub: ClientHubIcon,
    toolFinancialCents: FinancialCentsIcon,
    toolKarbon: KarbonIcon,
    toolMonday: MondayIcon,
    toolHubspot: HubSpotIcon
};
export const GetIntegrationsStepTID = (parentTestId)=>getTestIds({
        parentTestId
    });
const integrationsStepTextsPath = 'vendorOnboarding.integrationsStep';
export const IntegrationsStep = (param)=>{
    let { testId, values, onChange, onContinue, submitRef } = param;
    const testIds = useMemo(()=>GetIntegrationsStepTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys(integrationsStepTextsPath);
    const { api, logBI } = useStoreActions();
    const [otherDraft, setOtherDraft] = useState('');
    const [showOtherInput, setShowOtherInput] = useState(false);
    const toolOptions = useMemo(()=>{
        const labels = {
            toolQuickbooksOnline: t.toolQuickbooksOnline(),
            toolXero: t.toolXero(),
            toolGmail: t.toolGmail(),
            toolOutlook: t.toolOutlook(),
            toolSlack: t.toolSlack(),
            toolMake: t.toolMake(),
            toolZapier: t.toolZapier(),
            toolDouble: t.toolDouble(),
            toolClientHub: t.toolClientHub(),
            toolFinancialCents: t.toolFinancialCents(),
            toolKarbon: t.toolKarbon(),
            toolMonday: t.toolMonday(),
            toolHubspot: t.toolHubspot()
        };
        return TOOL_KEYS.map((key)=>{
            const Icon = TOOL_ICONS[key];
            return {
                key,
                label: key === 'toolDouble' ? undefined : labels[key],
                ariaLabel: key === 'toolDouble' ? labels[key] : undefined,
                icon: /*#__PURE__*/ _jsx(Icon, {
                    size: 24
                })
            };
        });
    }, [
        t
    ]);
    const commitOtherDraft = useCallback((value)=>{
        const trimmed = value.trim();
        if (trimmed.length === 0) {
            setShowOtherInput(false);
            setOtherDraft('');
            return;
        }
        if (!values.otherTools.includes(trimmed)) {
            onChange(_object_spread_props(_object_spread({}, values), {
                otherTools: [
                    ...values.otherTools,
                    trimmed
                ]
            }));
        }
        setOtherDraft('');
        setShowOtherInput(false);
    }, [
        onChange,
        values
    ]);
    const removeOther = useCallback((label)=>{
        onChange(_object_spread_props(_object_spread({}, values), {
            otherTools: values.otherTools.filter((entry)=>entry !== label)
        }));
    }, [
        onChange,
        values
    ]);
    const handleSubmit = useCallback(()=>{
        // TODO(ANC-13381): replace with real BI event id once assigned
        logBI(99999, {
            selectedTools: values.selectedTools,
            otherTools: values.otherTools,
            action: 'continue'
        });
        api.vendor.update({
            selectedTools: values.selectedTools,
            otherTools: values.otherTools
        }).catch(()=>{});
        onContinue();
    }, [
        api,
        logBI,
        onContinue,
        values
    ]);
    useImperativeHandle(submitRef, ()=>({
            submit: handleSubmit
        }), [
        handleSubmit
    ]);
    const extras = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            values.otherTools.map((label)=>/*#__PURE__*/ _jsx(Chip, {
                    label: label,
                    selected: true,
                    onClick: ()=>removeOther(label),
                    testId: "".concat(testIds.IntegrationsStepTestIds.OtherToolChip(), "-").concat(label)
                }, "other-".concat(label))),
            showOtherInput ? /*#__PURE__*/ _jsx(Chip, {
                inputMode: true,
                inputValue: otherDraft,
                onInputChange: setOtherDraft,
                onInputCommit: commitOtherDraft,
                inputPlaceholder: t.otherPlaceholder(),
                inputAutoFocus: true,
                testId: testIds.IntegrationsStepTestIds.OtherInput()
            }) : /*#__PURE__*/ _jsx(Chip, {
                label: "+ ".concat(t.otherChip()),
                onClick: ()=>setShowOtherInput(true),
                testId: testIds.IntegrationsStepTestIds.OtherChip()
            })
        ]
    });
    return /*#__PURE__*/ _jsx("div", {
        className: styles.root,
        "data-testid": testIds.IntegrationsStepTestIds.Root(),
        children: /*#__PURE__*/ _jsx(ChipCollection, {
            mode: "multi",
            options: toolOptions,
            value: values.selectedTools,
            onChange: (selectedTools)=>onChange(_object_spread_props(_object_spread({}, values), {
                    selectedTools
                })),
            extras: extras,
            testId: testIds.IntegrationsStepTestIds.ToolChip()
        })
    });
};
